const MAX_CONCURRENT_IMAGE_LOADS = 3;

let activeLoadCount = 0;
const pendingLoads: Array<() => void> = [];

function processNextLoad(): void {
  while (
    activeLoadCount < MAX_CONCURRENT_IMAGE_LOADS &&
    pendingLoads.length > 0
  ) {
    const loadFn = pendingLoads.shift()!;
    activeLoadCount++;
    loadFn();
  }
}

// Exported only for unit testing — do not use in production code.
export const __resetForTesting = (): void => {
  activeLoadCount = 0;
  pendingLoads.length = 0;
};

export function useImageLoadQueue() {
  /**
   * Enqueue an image load. `startLoad` receives a `release` callback that MUST
   * be called exactly once (on img load, error, or component unmount) to free
   * the concurrency slot. Returns a `cancel` function that removes the item
   * from the queue before it starts; calling cancel after the load has already
   * started is a no-op (the slot will be freed via `release`).
   */
  const enqueue = (startLoad: (release: () => void) => void): (() => void) => {
    let cancelled = false;

    const execute = (): void => {
      if (cancelled) {
        activeLoadCount--;
        processNextLoad();
        return;
      }

      let released = false;
      const release = (): void => {
        if (!released) {
          released = true;
          activeLoadCount--;
          processNextLoad();
        }
      };

      try {
        startLoad(release);
      } catch {
        release();
      }
    };

    if (activeLoadCount < MAX_CONCURRENT_IMAGE_LOADS) {
      activeLoadCount++;
      execute();
    } else {
      pendingLoads.push(execute);
    }

    return (): void => {
      cancelled = true;
      const idx = pendingLoads.indexOf(execute);
      if (idx !== -1) {
        pendingLoads.splice(idx, 1);
      }
    };
  };

  return { enqueue };
}
